import { ServiceCategoryService } from '@http/clients/reporting-app/service-category.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatCurrencyCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const ServiceCategories: IReportConfiguration<ServiceCategoryService> = {
    base: {
        type: ReportType.ServiceCategories,
        serviceType: ServiceCategoryService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        {
                            name: 'avg',
                            label: 'Avg'
                        },
                        {
                            name: 'min',
                            label: 'Min'
                        },
                        {
                            name: 'med',
                            label: 'Med'
                        },
                        {
                            name: 'max',
                            label: 'Max'
                        }
                    ],
                    rows: [
                        {
                            name: 'DirectCostPercentage',
                            label: 'Direct Cost (%)',
                            format: 'percent'
                        },
                        {
                            name: 'DirectCost',
                            label: 'Direct Cost ($)',
                            format: 'currency'
                        },
                        {
                            name: 'TotalDirectCostPercentage',
                            label: 'Total Direct Cost (%)',
                            format: 'percent'
                        }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Service Category by Avg DC ($)'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'category',
                            yKey: 'avgDirectCostPercentage',
                            yName: 'Avg Direct Cost (%)'
                        },
                        {
                            type: 'line',
                            xKey: 'category',
                            yKey: 'avgDirectCost',
                            yName: 'Avg Direct Cost ($)'
                        },
                        {
                            type: 'line',
                            xKey: 'category',
                            yKey: 'avgTotalDirectCostPercentage',
                            yName: 'Avg Total Direct Cost (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['avgDirectCost'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['avgDirectCostPercentage', 'avgTotalDirectCostPercentage'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPrice',
                            yName: 'Total Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalDirectCost',
                            yName: 'Total Direct Cost ($)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: ['totalSellPrice', 'totalDirectCost'],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'category',
                        headerName: 'Service Category Name ',
                        enableRowGroup: true,
                        lockVisible: true
                    },
                    {
                        field: 'used',
                        headerName: 'Total in Awarded Final Estimates (qty) '
                    },
                    {
                        field: 'totalSellPrice',
                        headerName: 'Total Sell Price',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'totalGrossMarginPercent',
                        headerName: 'Total Gross Margin (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'avgSellPrice',
                        headerName: 'Avg Sell Price',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'avgDirectCostPercentage',
                        headerName: 'Avg Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minDirectCostPercentage',
                        headerName: 'Min Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medDirectCostPercentage',
                        headerName: 'Med Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxDirectCostPercentage',
                        headerName: 'Max Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'avgDirectCost',
                        headerName: 'Avg Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'minDirectCost',
                        headerName: 'Min Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'medDirectCost',
                        headerName: 'Med Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'maxDirectCost',
                        headerName: 'Max Direct Cost ($)',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'avgTotalDirectCostPercentage',
                        headerName: 'Avg Total Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minTotalDirectCostPercentage',
                        headerName: 'Min Total Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medTotalDirectCostPercentage',
                        headerName: 'Med Total Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxTotalDirectCostPercentage',
                        headerName: 'Max Total Direct Cost (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
